import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const EMPTY = {
  slug: "",
  name_es: "",
  name_en: "",
  short_desc_es: "",
  short_desc_en: "",
  long_desc_es: "",
  long_desc_en: "",
  highlights_es: [],
  highlights_en: [],
  price: 0,
  currency: "EUR",
  image_url: "",
  gallery: [],
  stock: 0,
  low_stock_threshold: 6,
  active: true,
  bottle_count: 1,
  attributes: [],
};

function fieldStyle(err) {
  return `w-full bg-cream-100 border ${err ? "border-red-700" : "border-cream-400"} px-3 py-2 text-olive-900 text-sm font-light placeholder:text-olive-800/40 focus:outline-none focus:border-olive-700 transition-colors`;
}
const labelStyle = "block text-[10px] uppercase tracking-[0.24em] text-olive-800/60 mb-1.5";

export default function ProductEditor({ product, onClose, onSaved }) {
  const { authFetch } = useAdminAuth();
  const isEdit = Boolean(product?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState(null);

  useEffect(() => {
    if (product) {
      setForm({
        ...EMPTY,
        ...product,
        highlights_es: product.highlights_es || [],
        highlights_en: product.highlights_en || [],
        gallery: product.gallery || [],
        attributes: product.attributes || [],
      });
    } else {
      setForm(EMPTY);
    }
    setErrors({});
    setTopError(null);
  }, [product]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!/^[a-z0-9-]{2,120}$/.test(form.slug)) e.slug = true;
    if (!form.name_es || form.name_es.length < 2) e.name_es = true;
    if (!form.name_en || form.name_en.length < 2) e.name_en = true;
    if (!(Number(form.price) >= 0)) e.price = true;
    if (!(Number(form.stock) >= 0)) e.stock = true;
    if (!(Number(form.bottle_count) >= 1)) e.bottle_count = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    setTopError(null);
    if (!validate()) {
      setTopError("Revisa los campos marcados.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: parseInt(form.stock, 10),
        bottle_count: parseInt(form.bottle_count, 10),
        low_stock_threshold: parseInt(form.low_stock_threshold || 0, 10),
      };
      if (isEdit) {
        await authFetch(`/admin/products/${product.id}`, { method: "PUT", data: payload });
      } else {
        await authFetch(`/admin/products`, { method: "POST", data: payload });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al guardar.";
      setTopError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="product-editor"
      className="fixed inset-0 z-[9999] bg-charcoal/60 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
    >
      <div className="bg-cream-100 border border-cream-400 max-w-3xl w-full">
        <div className="flex items-center justify-between p-5 border-b border-cream-400">
          <h2 className="font-serif text-xl text-olive-900">
            {isEdit ? "Editar producto" : "Nuevo producto"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="product-editor-close"
            className="text-olive-800/60 hover:text-olive-900"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-8 space-y-6">
          {topError && (
            <div className="bg-red-50 border border-red-300 px-4 py-2.5 text-red-800 text-sm">
              {topError}
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelStyle}>Slug (URL)</label>
              <input
                value={form.slug}
                onChange={(e) => set("slug", e.target.value.toLowerCase())}
                placeholder="botella-500ml"
                data-testid="product-slug"
                className={fieldStyle(errors.slug)}
              />
            </div>
            <div>
              <label className={labelStyle}>Estado</label>
              <select
                value={form.active ? "active" : "draft"}
                onChange={(e) => set("active", e.target.value === "active")}
                data-testid="product-active"
                className={fieldStyle(false)}
              >
                <option value="active">Publicado</option>
                <option value="draft">No publicado</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Nombre (ES)</label>
              <input
                value={form.name_es}
                onChange={(e) => set("name_es", e.target.value)}
                data-testid="product-name-es"
                className={fieldStyle(errors.name_es)}
              />
            </div>
            <div>
              <label className={labelStyle}>Nombre (EN)</label>
              <input
                value={form.name_en}
                onChange={(e) => set("name_en", e.target.value)}
                data-testid="product-name-en"
                className={fieldStyle(errors.name_en)}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Descripción corta (ES)</label>
              <textarea
                rows={2}
                value={form.short_desc_es}
                onChange={(e) => set("short_desc_es", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
            <div>
              <label className={labelStyle}>Descripción corta (EN)</label>
              <textarea
                rows={2}
                value={form.short_desc_en}
                onChange={(e) => set("short_desc_en", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Descripción larga (ES)</label>
              <textarea
                rows={4}
                value={form.long_desc_es}
                onChange={(e) => set("long_desc_es", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
            <div>
              <label className={labelStyle}>Descripción larga (EN)</label>
              <textarea
                rows={4}
                value={form.long_desc_en}
                onChange={(e) => set("long_desc_en", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <label className={labelStyle}>Precio (EUR)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                data-testid="product-price"
                className={fieldStyle(errors.price)}
              />
            </div>
            <div>
              <label className={labelStyle}>Stock</label>
              <input
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => set("stock", e.target.value)}
                data-testid="product-stock"
                className={fieldStyle(errors.stock)}
              />
            </div>
            <div>
              <label className={labelStyle}>Aviso stock bajo</label>
              <input
                type="number"
                min="0"
                value={form.low_stock_threshold}
                onChange={(e) => set("low_stock_threshold", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
            <div>
              <label className={labelStyle}>Botellas / unidad</label>
              <input
                type="number"
                min="1"
                value={form.bottle_count}
                onChange={(e) => set("bottle_count", e.target.value)}
                className={fieldStyle(errors.bottle_count)}
              />
            </div>
          </div>

          <div>
            <label className={labelStyle}>Imagen principal (URL)</label>
            <input
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://…"
              data-testid="product-image-url"
              className={fieldStyle(false)}
            />
            {form.image_url ? (
              <div className="mt-3 w-32 h-32 bg-cream-200 border border-cream-400">
                <img
                  src={form.image_url}
                  alt={form.name_es || "preview"}
                  className="h-full w-full object-contain p-2"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}
          </div>

          <div>
            <label className={labelStyle}>Galería (una URL por línea)</label>
            <textarea
              rows={3}
              value={(form.gallery || []).join("\n")}
              onChange={(e) =>
                set(
                  "gallery",
                  e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                )
              }
              className={fieldStyle(false)}
              placeholder="https://…"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Highlights ES (uno por línea)</label>
              <textarea
                rows={4}
                value={(form.highlights_es || []).join("\n")}
                onChange={(e) =>
                  set(
                    "highlights_es",
                    e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                  )
                }
                className={fieldStyle(false)}
              />
            </div>
            <div>
              <label className={labelStyle}>Highlights EN (uno por línea)</label>
              <textarea
                rows={4}
                value={(form.highlights_en || []).join("\n")}
                onChange={(e) =>
                  set(
                    "highlights_en",
                    e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                  )
                }
                className={fieldStyle(false)}
              />
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-cream-400 flex items-center justify-end gap-3 bg-cream-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-olive-700/40 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="product-save"
            className="px-5 py-2.5 bg-olive-700 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-olive-900 disabled:opacity-60"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear producto"}
          </button>
        </div>
      </div>
    </div>
  );
}
